import { css } from "@emotion/react";
import { FC, ReactNode, useState } from "react";

import { ChevronRight } from "@livechat/design-system-icons";
import { DesignToken, Heading, Icon, RadiusToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { withVar } from "themes/helpers";

import { accordionBodyStyles, accordionBodyWrapperStyles, headerStyles, toggleVisibilityButtonStyles } from "./styles";

interface IProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  rightNode?: React.ReactNode;
  openByDefault?: boolean;
  onToggle?(isOpen: boolean): void;
  dataTestId?: string;
  disabled?: boolean;
  isOpen?: boolean;
}

interface IHeaderProps {
  title: string;
  icon?: React.ReactNode;
  rightNode?: React.ReactNode;
  onToggle?(): void;
  isOpen: boolean;
  dataTestId?: string;
  disabled?: boolean;
}

const AccordionHeader: FC<IHeaderProps> = ({ title, icon, rightNode, onToggle, isOpen, dataTestId, disabled }) => (
  <button css={headerStyles} onClick={onToggle} type="button" data-testid={dataTestId} disabled={disabled}>
    <Flex alignItems="center" gap="12px">
      {icon}
      <Heading size="xs" customColor={withVar(DesignToken.ContentBasicPrimary)}>
        {title}
      </Heading>
    </Flex>
    <Flex alignItems="center" gap="12px" data-testid={`${dataTestId}-right-nodes`}>
      {rightNode}
      <div css={toggleVisibilityButtonStyles(isOpen)}>
        <Icon source={ChevronRight} customColor={withVar(DesignToken.ContentBasicPrimary)} />
      </div>
    </Flex>
  </button>
);

const AccordionBody: FC<{ isOpen: boolean; children: ReactNode; dataTestId: string }> = ({
  isOpen,
  children,
  dataTestId,
}) => (
  <div css={accordionBodyWrapperStyles(isOpen)}>
    <div css={accordionBodyStyles(isOpen)} data-testid={dataTestId}>
      <Block paddingTop="16px">{children}</Block>
    </div>
  </div>
);

export const AccordionCard: FC<IProps> = ({
  title,
  icon,
  children,
  rightNode,
  openByDefault,
  onToggle,
  disabled,
  isOpen: isOpenProp,
  dataTestId = "accordion-card",
  ...props
}) => {
  const isControlled = isOpenProp !== undefined;
  const [isOpen, setIsOpen] = useState<boolean>(!!openByDefault);
  const isOpenState = isControlled ? isOpenProp : isOpen;

  const handleToggleContent = (): void => {
    if (onToggle) {
      onToggle(!isOpen);
    }
    setIsOpen(!isOpen);
  };

  return (
    <Flex
      column
      border={`1px solid ${withVar(DesignToken.BorderBasicPrimary)}`}
      borderRadius={withVar(RadiusToken.Radius3)}
      maxWidth="100%"
      color={withVar(DesignToken.ContentBasicPrimary)}
      data-testid={dataTestId}
      css={disabled && disabledStyles}
      aria-disabled={disabled}
      aria-expanded={isOpenState}
      {...props}
    >
      <AccordionHeader
        title={title}
        icon={icon}
        rightNode={rightNode}
        onToggle={handleToggleContent}
        isOpen={isOpen}
        dataTestId={`${dataTestId}-header`}
      />
      <AccordionBody isOpen={isOpenState} dataTestId={`${dataTestId}-body`}>
        {children}
      </AccordionBody>
    </Flex>
  );
};

const disabledStyles = css`
  opacity: 0.5;
  pointer-events: none;
`;
